import { ChangePasswordConfirmation } from "@/login/ChangePasswordConfirmation.tsx";
import { ChangePasswordError } from "@/login/ChangePasswordError.tsx";
import { ExternalLogin } from "@/login/ExernalLogin.tsx";
import { ForceResetPassword } from "@/login/ForceReset.tsx";
import { ForgotPassword } from "@/login/ForgotPassword.tsx";
import { Login } from "@/login/Login.tsx";
import { LoginAdminAs } from "@/login/LoginAdminAs.tsx";
import { LoginError } from "@/login/LoginError.tsx";
import { Timeout } from "@/login/LoginTimeout.tsx";
import { TimeoutSSO } from "@/login/LoginTimeoutSso.tsx";
import { Logout } from "@/login/Logout.tsx";
import { OauthFailure } from "@/login/OauthFailure.tsx";
import { OtpLogin } from "@/login/Otp.tsx";
import { PasswordChange } from "@/login/PasswordChange.tsx";
import { ResetPasswordError } from "@/login/ResetPasswordError.tsx";
import { ResetPasswordReceipt } from "@/login/ResetPasswordReceipt.tsx";
import { SelectDomain } from "@/login/SelectDomain.tsx";
import { Show as ViewsSelectOfficeShow } from "@/login/SelectOffice.tsx";
import { Troubleshoot } from "@/login/Troubleshoot.tsx";
import { UnblockLoginError } from "@/login/UnblockLoginError.tsx";
import { UnblockLoginReceipt } from "@/login/UnblockLoginReceipt.tsx";
import { UserAuthenticatedButDoesNotExistError } from "@/login/UserAuthenticatedButDoesNotExistError.tsx";
import { prefetchAllCountries } from "@/stores/Countries.ts";
import { prefetchCurrentUser } from "@/stores/CurrentUser.tsx";
import { prefetchLoggedInStatus } from "@/stores/LoggedInStatus.ts";
import type { CVPartnerReactRoute, Options } from "@/types.ts";
import { getCvPartner, skipFetchingUser } from "@/util/getCvPartner.ts";
import { createRouter } from "@/util/react.tsx";
import { NotFound } from "@/views/errors/404.tsx";
import { RouteError } from "@/views/errors/500.tsx";

export class LoginRouter implements CVPartnerReactRoute {
  data: Options["data"];

  constructor(options: Options) {
    this.data = options.data;
  }

  render() {
    const router = createRouter(
      [
        {
          children: [
            {
              path: "login",
              children: [
                {
                  index: true,
                  element: <Login type="normal" />,
                },
                {
                  path: "admin",
                  children: [
                    {
                      index: true,
                      element: (
                        <Login
                          action="/login/admin"
                          allowExtendedLogin={false}
                          type="support"
                        />
                      ),
                    },
                    {
                      path: "error",
                      element: (
                        <LoginError
                          message={this.data?.message}
                          errParams={this.data?.err_params}
                          supportEmail={this.data?.support_email}
                          action="/login/admin"
                          allowExtendedLogin={false}
                          type="support"
                        />
                      ),
                    },
                  ],
                },
                {
                  path: "otp",
                  element: <OtpLogin />,
                },
                {
                  path: "as",
                  element: <LoginAdminAs users={this.data?.users} />,
                },
                {
                  path: "external",
                  children: [
                    {
                      index: true,
                      element: <ExternalLogin />,
                    },
                    {
                      path: "error",
                      element: (
                        <LoginError
                          message={this.data?.message}
                          errParams={this.data?.err_params}
                          supportEmail={this.data?.support_email}
                          action="/login/external"
                          type="external"
                        />
                      ),
                    },
                  ],
                },
                {
                  path: "troubleshoot",
                  element: <Troubleshoot />,
                },
                {
                  path: "timeout",
                  element: <Timeout />,
                },
                {
                  path: "timeout_sso",
                  element: <TimeoutSSO />,
                },
                {
                  path: "error",
                  element: (
                    <LoginError
                      message={this.data?.message}
                      errParams={this.data?.err_params}
                      supportEmail={this.data?.support_email}
                      type="normal"
                    />
                  ),
                },
                {
                  path: "user_authenticated_but_does_not_exist_error",
                  element: (
                    <UserAuthenticatedButDoesNotExistError
                      federationGroupMembers={
                        this.data?.federation_group_members
                      }
                      field={this.data?.field}
                      user_id={this.data?.user_id}
                      supportEmail={this.data?.support_email}
                    />
                  ),
                },
                {
                  path: "select/domain",
                  element: (
                    <SelectDomain
                      googleAppsDomains={this.data?.google_apps_domains}
                    />
                  ),
                },
              ],
            },
            {
              path: "resetpassword",
              children: [
                {
                  path: "receipt",
                  element: <ResetPasswordReceipt />,
                },
                {
                  path: "error",
                  element: (
                    <ResetPasswordError
                      initial_password_error={this.data?.password_error}
                      user_name={this.data?.user_name}
                      user_birthday={this.data?.user_birthday}
                      submit_path={`/resetpassword/${this.data?.user_id}`}
                      reset_password_code={this.data?.reset_password_code}
                      user_id={this.data?.user_id}
                      display={{
                        title: "reset_password",
                        new_password: "enter_new_password",
                        submit: "save_new_password",
                      }}
                    />
                  ),
                },
                {
                  path: ":user_id",
                  element: (
                    <PasswordChange
                      type="reset"
                      initial_password_error={this.data?.password_error}
                      user_name={this.data?.user_name}
                      user_birthday={this.data?.user_birthday}
                      submit_path={`/resetpassword/${this.data?.user_id}`}
                      reset_password_code={this.data?.reset_password_code}
                      user_id={this.data?.user_id}
                      display={{
                        title: "reset_password",
                        new_password: "enter_new_password",
                        submit: "save_new_password",
                      }}
                    />
                  ),
                },
              ],
            },
            {
              path: "change_password",
              children: [
                {
                  index: true,
                  element: (
                    <PasswordChange
                      type="change"
                      initial_password_error={this.data?.password_error}
                      user_name={this.data?.user_name}
                      user_birthday={this.data?.user_birthday}
                      submit_path="/change_password"
                      user_id={this.data?.user_id}
                      display={{
                        title: "change_password",
                        old_password: "current_password",
                        new_password: "new_password",
                        submit: "change_password",
                      }}
                    />
                  ),
                },
                {
                  path: "confirmation",
                  element: (
                    <ChangePasswordConfirmation
                      initial_password_error={undefined}
                      user_name={this.data?.user_name}
                      user_birthday={this.data?.user_birthday}
                      submit_path="/change_password"
                      user_id={this.data?.user_id}
                      display={{
                        title: "change_password",
                        old_password: "current_password",
                        new_password: "new_password",
                        submit: "change_password",
                      }}
                    />
                  ),
                },
                {
                  path: "error",
                  element: (
                    <ChangePasswordError
                      message={this.data?.message}
                      initial_password_error={this.data?.password_error}
                      user_name={this.data?.user_name}
                      user_birthday={this.data?.user_birthday}
                      submit_path="/change_password"
                      user_id={this.data?.user_id}
                      display={{
                        title: "change_password",
                        old_password: "current_password",
                        new_password: "new_password",
                        submit: "change_password",
                      }}
                    />
                  ),
                },
              ],
            },
            {
              path: "unblock",
              children: [
                {
                  path: "receipt",
                  element: <UnblockLoginReceipt />,
                },
                {
                  path: "error",
                  element: <UnblockLoginError />,
                },
              ],
            },
            {
              path: "forgotpassword",
              element: <ForgotPassword />,
            },
            {
              path: "force_reset_password",
              element: <ForceResetPassword />,
            },
            {
              path: "select/office",
              element: <ViewsSelectOfficeShow />,
              loader: async () => {
                await Promise.all([
                  prefetchCurrentUser(),
                  prefetchAllCountries(),
                ]);

                return null;
              },
            },
            {
              path: "logout",
              element: <Logout />,
            },
            {
              path: "auth/failure",
              element: (
                <OauthFailure
                  message={this.data?.message}
                  errParams={this.data?.err_params}
                />
              ),
            },
            {
              path: "*",
              element: <NotFound />,
            },
          ],
          loader: async ({ request }) => {
            if (
              getCvPartner().isLoggedIn &&
              !skipFetchingUser(new URL(request.url).pathname)
            ) {
              await prefetchCurrentUser();
            } else {
              await prefetchLoggedInStatus();
            }

            return null;
          },
          errorElement: <RouteError />,
        },
      ],
      true,
    );

    return { router, selector: "container" };
  }
}
